import type { PortalUser } from "@/lib/api/login-user";

const AUTH_SESSION_KEY = "api-portal.auth.session";
/** Set on explicit logout; blocks restoring stale tab-scoped sessionStorage. */
const AUTH_LOGGED_OUT_KEY = "api-portal.auth.logged-out";
const AUTH_REMEMBER_ME_KEY = "api-portal.auth.remember-me";
const AUTH_SESSION_CHANGED_EVENT = "api-portal:auth-session-changed";

let authSessionSnapshot: AuthSession | null = null;
let authSessionRaw: string | null = null;
/** Guards migrateLegacySessionStorage while a login write is in flight. */
let persistAuthSessionInProgress = false;

function notifyAuthSessionChanged(): void {
  window.dispatchEvent(new Event(AUTH_SESSION_CHANGED_EVENT));
}

function invalidateAuthSessionCache(): void {
  authSessionRaw = null;
  authSessionSnapshot = null;
}

function markLoggedOut(): void {
  window.localStorage.setItem(AUTH_LOGGED_OUT_KEY, "1");
}

function clearLoggedOutFlag(): void {
  window.localStorage.removeItem(AUTH_LOGGED_OUT_KEY);
}

function isLoggedOut(): boolean {
  return window.localStorage.getItem(AUTH_LOGGED_OUT_KEY) === "1";
}

function purgeStaleAuthStorage(): void {
  window.localStorage.removeItem(AUTH_SESSION_KEY);
  window.sessionStorage.removeItem(AUTH_SESSION_KEY);
  invalidateAuthSessionCache();
}

function applyCrossTabAuthStorage(event: StorageEvent): void {
  if (event.key === AUTH_SESSION_KEY) {
    if (event.newValue === null) {
      window.sessionStorage.removeItem(AUTH_SESSION_KEY);
      // Explicit logout is signaled via AUTH_LOGGED_OUT_KEY. Clearing the shared
      // localStorage session during session-scoped login must not set logged-out.
      invalidateAuthSessionCache();
      notifyAuthSessionChanged();
    } else if (event.newValue !== null) {
      clearLoggedOutFlag();
      invalidateAuthSessionCache();
      notifyAuthSessionChanged();
    }
    return;
  }

  if (event.key === AUTH_LOGGED_OUT_KEY && event.newValue === "1") {
    window.sessionStorage.removeItem(AUTH_SESSION_KEY);
    invalidateAuthSessionCache();
    notifyAuthSessionChanged();
  }
}

/**
 * Cleans up duplicate legacy auth storage entries.
 * Session-scoped logins intentionally remain in sessionStorage only.
 */
function migrateLegacySessionStorage(): void {
  window.localStorage.removeItem("api-portal.auth.storage-ready");

  if (isLoggedOut()) {
    if (persistAuthSessionInProgress) {
      window.sessionStorage.removeItem(AUTH_SESSION_KEY);
      return;
    }
    purgeStaleAuthStorage();
    return;
  }

  const legacyRaw = window.sessionStorage.getItem(AUTH_SESSION_KEY);
  const localRaw = window.localStorage.getItem(AUTH_SESSION_KEY);

  if (localRaw && legacyRaw) {
    window.sessionStorage.removeItem(AUTH_SESSION_KEY);
  }
}

export interface AuthSession {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  user: PortalUser;
}

export type AuthSessionPersist = "local" | "session";

export interface SetAuthSessionOptions {
  persist?: AuthSessionPersist;
}

function readAuthSessionRaw(): string | null {
  const localRaw = window.localStorage.getItem(AUTH_SESSION_KEY);
  if (localRaw) {
    return localRaw;
  }
  return window.sessionStorage.getItem(AUTH_SESSION_KEY);
}

function readAuthSessionSnapshot(): AuthSession | null {
  migrateLegacySessionStorage();

  if (isLoggedOut()) {
    return null;
  }

  const raw = readAuthSessionRaw();
  if (raw === authSessionRaw) {
    return authSessionSnapshot;
  }

  authSessionRaw = raw;
  if (!raw) {
    authSessionSnapshot = null;
    return null;
  }

  try {
    authSessionSnapshot = JSON.parse(raw) as AuthSession;
  } catch {
    authSessionSnapshot = null;
  }

  return authSessionSnapshot;
}

function persistAuthSessionRaw(raw: string, persist: AuthSessionPersist): void {
  persistAuthSessionInProgress = true;
  try {
    if (persist === "local") {
      window.localStorage.setItem(AUTH_SESSION_KEY, raw);
      if (window.localStorage.getItem(AUTH_SESSION_KEY) !== raw) {
        throw new Error("Unable to persist auth session to localStorage.");
      }
      window.sessionStorage.removeItem(AUTH_SESSION_KEY);
    } else {
      window.sessionStorage.setItem(AUTH_SESSION_KEY, raw);
      window.localStorage.removeItem(AUTH_SESSION_KEY);
    }
    clearLoggedOutFlag();
  } catch (error) {
    window.localStorage.removeItem(AUTH_SESSION_KEY);
    window.sessionStorage.removeItem(AUTH_SESSION_KEY);
    throw error;
  } finally {
    persistAuthSessionInProgress = false;
  }
}

export function getRememberMePreference(): boolean {
  return window.localStorage.getItem(AUTH_REMEMBER_ME_KEY) === "1";
}

export function setRememberMePreference(remember: boolean): void {
  if (remember) {
    window.localStorage.setItem(AUTH_REMEMBER_ME_KEY, "1");
  } else {
    window.localStorage.removeItem(AUTH_REMEMBER_ME_KEY);
  }
}

export function setAuthSession(
  session: AuthSession,
  options?: SetAuthSessionOptions,
): void {
  const persist = options?.persist ?? "session";
  const raw = JSON.stringify(session);
  persistAuthSessionRaw(raw, persist);
  authSessionSnapshot = session;
  authSessionRaw = raw;
  notifyAuthSessionChanged();
}

export function getAuthSession(): AuthSession | null {
  return readAuthSessionSnapshot();
}

/** Stable snapshot for `useSyncExternalStore` — returns the same reference until the session changes. */
export function getAuthSessionSnapshot(): AuthSession | null {
  return readAuthSessionSnapshot();
}

export function clearAuthSession(): void {
  window.localStorage.removeItem(AUTH_SESSION_KEY);
  window.sessionStorage.removeItem(AUTH_SESSION_KEY);
  markLoggedOut();
  authSessionSnapshot = null;
  authSessionRaw = null;
  notifyAuthSessionChanged();
}

export function subscribeAuthSession(onChange: () => void): () => void {
  const handler = () => onChange();

  window.addEventListener(AUTH_SESSION_CHANGED_EVENT, handler);
  return () => {
    window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, handler);
  };
}

export function getAccessToken(): string | null {
  return getAuthSession()?.accessToken ?? null;
}

/** First token of `contact_name` for greetings and nav (e.g. "Jane Doe" → "Jane"). */
export function getContactFirstName(contactName: string): string {
  const trimmed = contactName.trim();
  if (!trimmed) {
    return "Developer";
  }

  return trimmed.split(/\s+/)[0] ?? trimmed;
}

// Promote any tab-scoped legacy session before the first React render.
migrateLegacySessionStorage();

const crossTabStorageFlag = "__apiPortalAuthStorageListening";
const windowWithAuthFlag = window as Window & { [crossTabStorageFlag]?: boolean };
if (!windowWithAuthFlag[crossTabStorageFlag]) {
  windowWithAuthFlag[crossTabStorageFlag] = true;
  window.addEventListener("storage", applyCrossTabAuthStorage);
}
